import type {
  ExecutedLibraryView,
  LibraryViewSaveRequest,
  MdbaseLibraryView,
} from "./mdbase-library-views.js";
import type {
  Annotation,
  AnnotationDeletionPlan,
  AnnotationCreationRequest,
  ExportedCollectionFile,
  FileId,
  FileRevision,
  ReaderRequestOptions,
  ReadingPosition,
  Source,
  SourceId,
  SourceSummary,
  SourceFileImportRequest,
  SourceImportOptions,
  SourceTextSearchMatch,
  CitationCandidate,
  CitationResolutionRequest,
} from "@mdbase-reader/core";

export interface ReaderLibrarySnapshot {
  readonly collectionName: string;
  readonly sources: readonly SourceSummary[];
  readonly connectionState: "connected" | "offline" | "syncing";
  readonly sourceIndex?: {
    readonly loaded: number;
    readonly total?: number;
    readonly complete: boolean;
  };
}

export interface ReaderLibraryRequestOptions extends ReaderRequestOptions {
  readonly onProgress?: (snapshot: ReaderLibrarySnapshot) => void;
}

export interface ReaderWorkspaceGateway {
  library(options?: ReaderLibraryRequestOptions): Promise<ReaderLibrarySnapshot>;
  listLibraryViews(options?: ReaderRequestOptions): Promise<readonly MdbaseLibraryView[]>;
  executeLibraryView(
    view: MdbaseLibraryView,
    options?: ReaderRequestOptions,
  ): Promise<ExecutedLibraryView>;
  saveLibraryView(
    request: LibraryViewSaveRequest,
    options?: ReaderRequestOptions,
  ): Promise<MdbaseLibraryView>;
  source(id: SourceId, options?: ReaderRequestOptions): Promise<Source | null>;
  annotations(id: SourceId, options?: ReaderRequestOptions): Promise<readonly Annotation[]>;
  annotationSourceIds?(options?: ReaderRequestOptions): Promise<readonly SourceId[]>;
  saveSourceBody(source: Source, body: string, options?: ReaderRequestOptions): Promise<Source>;
  saveSourceCitation(
    source: Source,
    citation: unknown,
    options?: ReaderRequestOptions,
  ): Promise<Source>;
  resolveCitation?(
    request: CitationResolutionRequest,
    options?: ReaderRequestOptions,
  ): Promise<CitationCandidate>;
  searchText(
    query: string,
    options?: ReaderRequestOptions,
  ): Promise<readonly SourceTextSearchMatch[]>;
  readFile(
    file: string,
    expectedRevision?: FileRevision,
    options?: ReaderRequestOptions,
  ): Promise<ExportedCollectionFile>;
  importSourceFile(
    request: Omit<SourceFileImportRequest, "collectionId">,
    options?: SourceImportOptions,
  ): Promise<Source>;
  createAnnotation(
    request: AnnotationCreationRequest,
    options?: ReaderRequestOptions,
  ): Promise<Annotation>;
  updateAnnotation(
    annotation: Annotation,
    body: string,
    options?: ReaderRequestOptions,
  ): Promise<Annotation>;
  planAnnotationDeletion(
    annotation: Annotation,
    options?: ReaderRequestOptions,
  ): Promise<AnnotationDeletionPlan>;
  deleteAnnotation(
    annotation: Annotation,
    plan: AnnotationDeletionPlan,
    options?: ReaderRequestOptions,
  ): Promise<void>;
  transcludeAnnotation(
    source: Source,
    annotation: Annotation,
    options?: ReaderRequestOptions,
  ): Promise<Source>;
  saveReadingPosition(
    source: Source,
    documentFileId: FileId,
    position: ReadingPosition,
    options?: ReaderRequestOptions,
  ): Promise<Source>;
}

export function filterSources(
  sources: readonly SourceSummary[],
  search: string,
): readonly SourceSummary[] {
  const normalized = search.trim().toLocaleLowerCase();
  if (!normalized) {
    return sources;
  }
  return sources.filter((source) =>
    [
      source.title,
      ...source.creators,
      ...source.tags,
      source.publication,
      source.site,
      source.published === undefined ? undefined : String(source.published),
    ]
      .filter((value): value is string => value !== undefined)
      .join("\n")
      .toLocaleLowerCase()
      .includes(normalized),
  );
}
